import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { register, isLoading } = useAuthStore();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const isRegistrationEnabled = source === 'lgs';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await register(name, email, password);
      toast.success('Account created successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="relative w-full max-w-md">
        <div className="glass rounded-2xl p-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-black" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Manrope']">InSynq</h1>
          </div>

          <h2 className="text-xl font-semibold text-white text-center mb-2">Create your account</h2>
          <p className="text-zinc-400 text-center mb-6 text-sm">Start tracking your team&apos;s productivity</p>

          {isRegistrationEnabled ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Full Name</label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-zinc-950 border-zinc-800 focus:border-cyan-500"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-zinc-950 border-zinc-800 focus:border-cyan-500"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-zinc-950 border-zinc-800 focus:border-cyan-500"
                  minLength={6}
                  required
                  data-testid="register-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-white text-black hover:bg-zinc-200 font-medium"
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Create Account
              </Button>
            </form>
          ) : (
            <div className="text-center py-8 px-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <p className="text-yellow-500 font-medium mb-2">Onboarding Paused</p>
              <p className="text-zinc-400 text-sm">
                New account creation is currently paused. Please check back later or contact support if you have an invite code.
              </p>
            </div>
          )}

          <p className="text-center text-sm text-zinc-400 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
